import { Controller } from '@hotwired/stimulus'
import { allCuratedCodepoints, codepointsForBlock } from '../lib/unicode_blocks.js'
import {
  categoryNeedsUnicodeNamesForSearch,
  codepointMatchesSearch,
  ensureUnicodeNames,
  getCharacterMeta,
  isCuratedCodepoint,
} from '../lib/character_correlations.js'
import {
  DEFAULT_GLYPH_SIZE,
  normalizeGlyphSize,
} from '../lib/char_glyph_sizes.js'
import {
  buildCategoryBadgesHtml,
  buildCodepointFieldsHtml,
} from '../lib/char_detail_fields.js'

export default class extends Controller {
  static targets = [
    'overlay',
    'grid',
    'modal',
    'modalGlyph',
    'modalName',
    'modalCodes',
    'modalFormulaText',
    'modalSheetArt',
    'fullscreenButton',
    'toast',
  ]

  static values = {
    sheetArtExamples: Array,
  }

  connect() {
    this.isOpen = false
    this.gridRendered = false
    this.nameSearchQuery = ''
    this.nameSearchNamesPromise = null
    this.glyphSize = DEFAULT_GLYPH_SIZE
    this.selectedCp = null
    this.currentFormula = null
    this.allCodepoints = null
    this.gridOrder = null

    if (this.hasOverlayTarget) {
      this.applyWallSize(this.charactersController()?.glyphSize ?? DEFAULT_GLYPH_SIZE, { persist: false })
    }

    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundPopstate = this.handlePopstate.bind(this)
    this.boundResizeToolbar = this.syncToolbarOffset.bind(this)

    window.addEventListener('popstate', this.boundPopstate)
    window.addEventListener('resize', this.boundResizeToolbar)

    if (typeof ResizeObserver !== 'undefined') {
      const toolbar = document.getElementById('char-toolbar')
      if (toolbar) {
        this.toolbarResizeObserver = new ResizeObserver(this.boundResizeToolbar)
        this.toolbarResizeObserver.observe(toolbar)
      }
    }

    if (window.location.hash === '#wall') {
      requestAnimationFrame(() => this.open({ skipHash: true }))
    }
  }

  disconnect() {
    this.unlockScroll()
    document.body.classList.remove('char-wall-open')
    document.removeEventListener('keydown', this.boundKeydown)
    window.removeEventListener('popstate', this.boundPopstate)
    window.removeEventListener('resize', this.boundResizeToolbar)
    this.toolbarResizeObserver?.disconnect()
    clearTimeout(this.toastTimeout)
  }

  toggle(event) {
    event?.preventDefault()
    if (this.isOpen) {
      this.close(event)
    } else {
      this.open(event)
    }
  }

  open(eventOrOptions = {}) {
    const event = eventOrOptions?.preventDefault ? eventOrOptions : null
    const { skipHash = false } = event ? {} : eventOrOptions

    event?.preventDefault()
    if (!this.hasOverlayTarget || this.isOpen) return

    const characters = this.charactersController()
    this.nameSearchQuery = characters?.nameSearchQuery || ''
    this.applyWallSize(characters?.glyphSize ?? this.glyphSize, { persist: false })

    this.isOpen = true
    this.overlayTarget.classList.remove('hidden')
    this.overlayTarget.setAttribute('aria-hidden', 'false')
    document.body.classList.add('char-wall-open')
    this.syncToolbarOffset()
    this.updateFullscreenButton()
    this.lockScroll()
    document.addEventListener('keydown', this.boundKeydown)

    if (!this.gridRendered) {
      this.ensureCodepoints()
      this.renderGrid()
      this.gridRendered = true
    } else {
      this.renderGrid()
    }

    if (!skipHash && window.location.hash !== '#wall') {
      window.history.pushState({ charWall: true }, '', '#wall')
    }
  }

  close(eventOrOptions = {}) {
    const event = eventOrOptions?.preventDefault ? eventOrOptions : null
    const { skipHash = false } = event ? {} : eventOrOptions

    event?.preventDefault()
    if (!this.hasOverlayTarget || !this.isOpen) return

    this.isOpen = false
    this.overlayTarget.classList.add('hidden')
    this.overlayTarget.setAttribute('aria-hidden', 'true')
    document.body.classList.remove('char-wall-open')
    this.updateFullscreenButton()
    this.closeModal({ skipFocus: true })
    this.unlockScroll()
    document.removeEventListener('keydown', this.boundKeydown)

    if (!skipHash && window.location.hash === '#wall') {
      const url = `${window.location.pathname}${window.location.search}`
      window.history.pushState({}, '', url)
    }
  }

  handlePopstate() {
    if (window.location.hash === '#wall') {
      if (!this.isOpen) this.open({ skipHash: true })
      return
    }

    if (this.isOpen) this.close({ skipHash: true })
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return

    const characters = this.charactersController()
    if (characters?.textColorOpen) {
      characters.closeTextColorPopover()
      return
    }

    if (characters?.nameSearchOpen) {
      characters.closeNameSearchPanel({ focusToggle: true })
      return
    }

    if (this.hasModalTarget && !this.modalTarget.classList.contains('hidden')) {
      this.closeModal(event)
      return
    }

    this.close(event)
  }

  lockScroll() {
    document.body.style.overflow = 'hidden'
  }

  unlockScroll() {
    document.body.style.overflow = ''
  }

  syncToolbarOffset() {
    const toolbar = document.getElementById('char-toolbar')
    if (!toolbar) return

    const height = Math.ceil(toolbar.getBoundingClientRect().height)
    document.documentElement.style.setProperty('--char-wall-toolbar-height', `${height}px`)
  }

  updateFullscreenButton() {
    if (!this.hasFullscreenButtonTarget) return

    const pressed = this.isOpen
    this.fullscreenButtonTarget.setAttribute('aria-pressed', String(pressed))
    this.fullscreenButtonTarget.classList.toggle('bg-slate-100', pressed)

    if (pressed) {
      this.fullscreenButtonTarget.setAttribute('aria-label', 'Exit fullscreen character browser')
      this.fullscreenButtonTarget.title = 'Exit fullscreen character browser'
    } else {
      this.fullscreenButtonTarget.setAttribute('aria-label', 'Fullscreen character browser')
      this.fullscreenButtonTarget.title = 'Fullscreen character browser'
    }
  }

  charactersController() {
    return this.application.getControllerForElementAndIdentifier(this.element, 'characters')
  }

  syncSearch(query) {
    this.nameSearchQuery = query
    this.ensureNameSearchNamesLoaded()
    this.renderGrid()
  }

  searchByCode(codepoint) {
    if (!this.validCodepoint(codepoint)) return

    this.nameSearchQuery = ''
    this.renderGrid()
    this.selectGlyph(codepoint)
  }

  syncGlyphSize(size) {
    this.applyWallSize(size, { persist: false })
  }

  ensureCodepoints() {
    if (this.allCodepoints) return

    this.allCodepoints = allCuratedCodepoints()
    this.gridOrder = [...this.allCodepoints]
  }

  renderGrid() {
    if (!this.hasGridTarget) return

    this.ensureCodepoints()

    const characters = this.charactersController()
    let codepoints = codepointsForBlock(characters?.gridBlockForSearch() ?? { id: 'all' })
    const queryLower = this.nameSearchQuery.toLowerCase()

    if (queryLower) {
      codepoints = codepoints.filter((cp) => codepointMatchesSearch(cp, queryLower))
    }

    const fragment = document.createDocumentFragment()

    if (!codepoints.length && queryLower) {
      const message = document.createElement('p')
      message.className = 'col-span-full py-12 text-center text-sm text-slate-500'
      message.textContent = `No characters match "${this.nameSearchQuery}". Try a decimal code like 9733 for ★, or clear the search.`
      fragment.appendChild(message)
    } else {
      codepoints.forEach((cp) => {
        fragment.appendChild(this.buildGlyphCell(cp))
      })
    }

    this.gridTarget.replaceChildren(fragment)
    this.highlightSelectedCell()
  }

  buildGlyphCell(codepoint) {
    const button = document.createElement('button')
    button.type = 'button'
    button.dataset.codepoint = String(codepoint)
    button.dataset.action = 'click->char-wall#selectGlyphFromEvent'
    button.title = this.glyphLabel(codepoint)
    button.setAttribute('aria-label', `${this.glyphLabel(codepoint)}, ${this.charFormula(codepoint)}`)
    button.className =
      'char-glyph-cell char-glyph flex items-center justify-center aspect-square rounded-lg bg-transparent border-0 cursor-pointer transition-[transform,box-shadow,background-color] duration-150 ease-out hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50'
    button.setAttribute('aria-pressed', 'false')

    const span = document.createElement('span')
    span.className = 'char-glyph pointer-events-none select-none leading-none'
    span.textContent = this.glyphFor(codepoint)
    button.appendChild(span)

    return button
  }

  selectGlyphFromEvent(event) {
    event.preventDefault()
    const cp = parseInt(event.currentTarget.dataset.codepoint, 10)
    if (Number.isNaN(cp)) return
    this.selectGlyph(cp)
  }

  selectGlyph(codepoint) {
    if (!this.validCodepoint(codepoint)) return

    this.selectedCp = codepoint
    this.highlightSelectedCell()
    this.openModal(codepoint)
    this.loadUnicodeNameIfNeeded(codepoint)
  }

  openModal(codepoint) {
    if (!this.hasModalTarget) return

    const meta = getCharacterMeta(codepoint)
    const glyph = this.glyphFor(codepoint)
    const formula = this.charFormula(codepoint)

    this.currentFormula = formula

    if (this.hasModalGlyphTarget) {
      this.modalGlyphTarget.textContent = glyph
      this.modalGlyphTarget.dataset.codepoint = String(codepoint)
      this.modalGlyphTarget.setAttribute('aria-label', `Copy character: ${meta.name}`)
      this.modalGlyphTarget.title = `${meta.name}. Click to copy the character.`
    }

    if (this.hasModalNameTarget) {
      this.modalNameTarget.textContent = meta.name
    }

    if (this.hasModalCodesTarget) {
      this.modalCodesTarget.innerHTML =
        buildCategoryBadgesHtml(codepoint, this.escapeHtml.bind(this)) +
        buildCodepointFieldsHtml(codepoint, this.escapeHtml.bind(this))
    }

    if (this.hasModalFormulaTextTarget) {
      this.modalFormulaTextTarget.textContent = formula
    }

    this.renderModalSheetArt(codepoint)

    this.modalTarget.classList.remove('hidden')
    this.modalTarget.setAttribute('aria-hidden', 'false')
  }

  renderModalSheetArt(codepoint) {
    if (!this.hasModalSheetArtTarget) return

    const examples = this.sheetArtExamplesFor(codepoint)
    this.modalSheetArtTarget.replaceChildren()

    if (!examples.length) return

    examples.forEach((example) => {
      this.modalSheetArtTarget.appendChild(this.buildSheetArtCard(example))
    })
  }

  sheetArtExamplesFor(codepoint) {
    return (this.sheetArtExamplesValue || []).filter((example) => example.codepoints?.includes(codepoint))
  }

  buildSheetArtCard(example) {
    const card = document.createElement('button')
    card.type = 'button'
    card.className =
      'shrink-0 w-52 rounded-xl bg-slate-50 p-3 text-left ring-1 ring-slate-200 hover:bg-slate-100 active:bg-slate-200/70 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50'
    card.setAttribute('aria-label', `Copy formula: ${example.title}`)
    card.title = 'Click to copy formula to clipboard'

    const preview = document.createElement('p')
    preview.className = 'char-glyph font-mono text-base truncate mb-2'
    preview.title = example.preview
    preview.textContent = example.preview
    card.appendChild(preview)

    const title = document.createElement('p')
    title.className = 'text-sm font-semibold text-slate-900 line-clamp-2'
    title.textContent = example.title
    card.appendChild(title)

    const formula = document.createElement('code')
    formula.className = 'char-page__mono block mt-2 text-xs text-slate-600 truncate'
    formula.textContent = example.formula
    card.appendChild(formula)

    card.addEventListener('click', (event) => {
      event.preventDefault()
      this.copyFormulaText(example.formula, card)
    })

    return card
  }

  loadUnicodeNameIfNeeded(codepoint) {
    if (isCuratedCodepoint(codepoint)) return

    ensureUnicodeNames().then(() => {
      if (this.selectedCp !== codepoint) return
      if (!this.hasModalTarget || this.modalTarget.classList.contains('hidden')) return

      const meta = getCharacterMeta(codepoint)
      if (this.hasModalNameTarget) this.modalNameTarget.textContent = meta.name
      if (this.hasModalGlyphTarget) {
        this.modalGlyphTarget.setAttribute('aria-label', `Copy character: ${meta.name}`)
        this.modalGlyphTarget.title = `${meta.name}. Click to copy the character.`
      }
    })
  }

  highlightSelectedCell() {
    if (!this.hasGridTarget) return

    this.gridTarget.querySelectorAll('.char-glyph-cell').forEach((cell) => {
      const cp = parseInt(cell.dataset.codepoint, 10)
      const selected = this.selectedCp === cp
      cell.classList.toggle('is-selected', selected)
      cell.classList.toggle('-translate-y-px', selected)
      cell.classList.toggle('bg-emerald-500/[0.06]', selected)
      cell.setAttribute('aria-pressed', String(selected))
    })
  }

  closeModal(event) {
    event?.preventDefault()

    if (!this.hasModalTarget) return

    this.modalTarget.classList.add('hidden')
    this.modalTarget.setAttribute('aria-hidden', 'true')
  }

  applyWallSize(size, { persist = true } = {}) {
    if (!this.hasOverlayTarget) return

    this.glyphSize = normalizeGlyphSize(size)
    this.overlayTarget.dataset.wallSize = this.glyphSize
  }

  ensureNameSearchNamesLoaded() {
    this.ensureCodepoints()
    if (!categoryNeedsUnicodeNamesForSearch(this.allCodepoints)) return

    if (!this.nameSearchNamesPromise) {
      this.nameSearchNamesPromise = ensureUnicodeNames().then(() => {
        if (this.nameSearchQuery) this.renderGrid()
      })
    }
  }

  copyGlyph(event) {
    event?.preventDefault()
    if (!this.selectedCp) return
    this.copyTextToClipboard(this.glyphFor(this.selectedCp), event?.currentTarget, {
      successMessage: 'Character copied to clipboard',
    })
  }

  copyFormula(event) {
    event?.preventDefault()
    if (!this.currentFormula) return
    this.copyFormulaText(this.currentFormula, event?.currentTarget)
  }

  async copyFormulaText(formula, button) {
    const text = formula.startsWith('=') ? formula : `=${formula}`
    await this.copyTextToClipboard(text, button, { successMessage: 'Formula copied to clipboard' })
  }

  async copyTextToClipboard(text, button, { successMessage = 'Copied to clipboard' } = {}) {
    if (button?.dataset.copying === 'true') return

    if (button) button.dataset.copying = 'true'

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else if (!this.fallbackCopyText(text)) {
        throw new Error('clipboard unavailable')
      }

      this.showToast(successMessage)

      if (button && button.tagName === 'BUTTON' && !button.dataset.copyIcon) {
        const original = button.textContent
        button.textContent = 'Copied'
        setTimeout(() => {
          button.textContent = original
        }, 2000)
      }
    } catch {
      this.showToast('Copy failed. Select the text and press Cmd+C or Ctrl+C.')
    } finally {
      if (button) delete button.dataset.copying
    }
  }

  fallbackCopyText(text) {
    try {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      const copied = document.execCommand('copy')
      document.body.removeChild(textarea)
      return copied
    } catch {
      return false
    }
  }

  showToast(message) {
    if (!this.hasToastTarget) return

    this.toastTarget.textContent = message
    this.toastTarget.classList.remove('hidden', 'opacity-0')
    clearTimeout(this.toastTimeout)
    this.toastTimeout = setTimeout(() => {
      this.toastTarget.classList.add('opacity-0')
      setTimeout(() => this.toastTarget.classList.add('hidden'), 200)
    }, 2000)
  }

  validCodepoint(cp) {
    return Number.isInteger(cp) && cp >= 0 && cp <= 0x10ffff
  }

  charFormula(codepoint) {
    return `=CHAR(${codepoint})`
  }

  glyphFor(codepoint) {
    if (codepoint === 10) return '↵'
    if (codepoint === 9) return '⇥'
    try {
      return String.fromCodePoint(codepoint)
    } catch {
      return '?'
    }
  }

  glyphLabel(codepoint) {
    return getCharacterMeta(codepoint).name
  }

  escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }
}
